import { useMemo } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import type { ApiError } from "@/lib/api/unwrap";

import {
  useAddOptionalIssue,
  useAvailableOptionalIssues,
  useIssues,
  useOptionalIssues,
  useUnavoidableIssues,
} from "../api";
import { ErrorBlock, LoadingBlock } from "../components/QueryState";

interface IssuesStepProps {
  onContinue: () => void;
}

export function IssuesStep({ onContinue }: IssuesStepProps) {
  const { t } = useTranslation();
  const unavoidable = useUnavoidableIssues();
  const allIssues = useIssues();
  const optional = useOptionalIssues();
  const available = useAvailableOptionalIssues();
  const addIssue = useAddOptionalIssue();

  const chosenIssues = useMemo(() => {
    const byId = new Map((allIssues.data ?? []).map((i) => [i.id, i]));
    return (optional.data ?? []).map((choice) => ({
      choiceId: choice.id,
      issue: byId.get(choice.issue),
    }));
  }, [optional.data, allIssues.data]);

  // The server decides what can be added: an issue with no action for this
  // company would be chosen and then never appear in the questionnaire.
  const availableIssues = available.data ?? [];

  if (
    unavoidable.isLoading ||
    allIssues.isLoading ||
    optional.isLoading ||
    available.isLoading
  ) {
    return <LoadingBlock />;
  }
  if (unavoidable.error) return <ErrorBlock error={unavoidable.error} />;
  if (allIssues.error) return <ErrorBlock error={allIssues.error} />;
  if (available.error) return <ErrorBlock error={available.error} />;

  const onError = (err: unknown) =>
    toast.error((err as ApiError).detail ?? t("questionnaire.saveError"));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("questionnaire.issues.unavoidableTitle")}</CardTitle>
          <CardDescription>
            {t("questionnaire.issues.unavoidableSubtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {unavoidable.data && unavoidable.data.length > 0 ? (
            <ul className="flex flex-wrap gap-2">
              {unavoidable.data.map((issue) => (
                <li
                  key={issue.id}
                  className="rounded-md bg-secondary px-3 py-1.5 text-sm text-secondary-foreground"
                >
                  {issue.name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("questionnaire.issues.noUnavoidable")}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("questionnaire.issues.optionalTitle")}</CardTitle>
          <CardDescription>
            {t("questionnaire.issues.optionalSubtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="mb-2 text-sm font-medium">
              {t("questionnaire.issues.selected")}
            </h3>
            {chosenIssues.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {/* No remove control: as in the Django picker, an issue added
                    to the self-assessment stays in it. */}
                {chosenIssues.map(({ choiceId, issue }) => (
                  <li key={choiceId}>
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-primary bg-primary/5 px-3 py-1.5 text-sm">
                      {issue?.name ?? t("questionnaire.issues.unknownIssue")}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("questionnaire.issues.noneSelected")}
              </p>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">
              {t("questionnaire.issues.available")}
            </h3>
            {availableIssues.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {availableIssues.map((issue) => (
                  <li key={issue.id}>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={addIssue.isPending}
                      onClick={() => addIssue.mutate(issue.id, { onError })}
                    >
                      <Plus className="size-4" aria-hidden />
                      {issue.name}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("questionnaire.issues.noneAvailable")}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="button" onClick={onContinue}>
          {t("questionnaire.continue")}
        </Button>
      </div>
    </div>
  );
}
